import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
    Card,
    CardHeader,
    CardContent,
    CircularProgress,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Button,
    Box,
    Toolbar,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TextField
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import inMemoryJWT from '../../../inMemoryJwt';

const authorizedRequest = async (requestFn, onSuccess, onFailure) => {
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');

    try {
        const response = await requestFn(token);
        onSuccess(response);
    } catch (error) {
        if (error.response?.status === 401 && refreshToken) {
            const refreshed = await inMemoryJWT.refreshTokenGen(refreshToken);
            if (refreshed) {
                const newToken = localStorage.getItem('token');
                try {
                    const retryResponse = await requestFn(newToken);
                    onSuccess(retryResponse);
                } catch {
                    onFailure?.();
                }
            } else {
                onFailure?.();
            }
        } else {
            onFailure?.();
        }
    }
};

export const HvgUserOrdersList = () => {
    const { id } = useParams(); // user ID
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [username, setUsername] = useState('');
    const [error, setError] = useState(null);

    const handleDelete = (orderId) => {
        if (window.confirm('Are you sure you want to delete this order?')) {
            authorizedRequest(
                (token) =>
                    axios.delete(`${process.env.REACT_APP_API_URL}/hvg-userEditionOrders/${orderId}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    }),
                () => {
                    setOrders((prev) => prev.filter((o) => o.id !== orderId));
                },
                () => alert('Delete failed')
            );
        }
    };

    useEffect(() => {
        const token = localStorage.getItem('token');
        const refreshToken = localStorage.getItem('refreshToken');

        const fetchData = async (authToken) => {
            return Promise.all([
                axios.get(`${process.env.REACT_APP_API_URL}/hvg-userEditionOrders/user/${id}`, {
                    headers: { Authorization: `Bearer ${authToken}` },
                }),
                axios.get(`${process.env.REACT_APP_API_URL}/users/${id}`, {
                    headers: { Authorization: `Bearer ${authToken}` },
                }),
            ]);
        };

        const loadOrders = async () => {
            try {
                const [ordersRes, userRes] = await fetchData(token);
                setOrders(ordersRes.data);
                setUsername(userRes.data.username);
                setLoading(false);
            } catch (error) {
                if (error.response?.status === 401 && refreshToken) {
                    try {
                        const refreshed = await inMemoryJWT.refreshTokenGen(refreshToken);
                        if (refreshed) {
                            const newToken = localStorage.getItem('token');
                            const [ordersRes, userRes] = await fetchData(newToken);
                            setOrders(ordersRes.data);
                            setUsername(userRes.data.username);
                            setLoading(false);
                        } else {
                            setError(error);
                            setLoading(false);
                        }
                    } catch {
                        setError(error);
                        setLoading(false);
                    }
                } else {
                    setError(error);
                    setLoading(false);
                }
            }
        };

        loadOrders();
    }, [id]);

    if (loading) return <CircularProgress />;
    if (error) return <Typography color="error">Error loading orders.</Typography>;

    return (
        <Card>
            <Toolbar sx={{ justifyContent: 'space-between' }}>
                <Typography variant="h6" sx={{ ml: 2 }}>
                    HVG Edition Orders for <strong>{username}</strong>
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate(`/admin/hvgorders/user/${id}/create`)}
                >
                    Create Order
                </Button>
            </Toolbar>

            <CardContent>
                {orders.length === 0 ? (
                    <Typography>No orders found for this user.</Typography>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Edition</strong></TableCell>
                                <TableCell><strong>Season</strong></TableCell>
                                <TableCell><strong>Orders</strong></TableCell>
                                <TableCell><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {orders.map((order) => (
                                <TableRow key={order.id}>
                                    <TableCell>{order.edition?.title}</TableCell>
                                    <TableCell>{order.edition?.season}</TableCell>
                                    <TableCell>{order.defaultOrders}</TableCell>
                                    <TableCell>
                                        <Button
                                            size="small"
                                            startIcon={<EditIcon />}
                                            onClick={() =>
                                                navigate(`/admin/hvgorders/user/${id}/edit/${order.id}`)
                                            }
                                        >
                                            Edit
                                        </Button>
                                        <Button
                                            size="small"
                                            color="error"
                                            sx={{ ml: 1 }}
                                            onClick={() => handleDelete(order.id)}
                                        >
                                            Delete
                                        </Button>

                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
        </Card>
    );
};

export const HvgUserOrderCreate = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [editions, setEditions] = useState([]);
    const [selectedEditionId, setSelectedEditionId] = useState('');
    const [defaultOrders, setDefaultOrders] = useState('');

    useEffect(() => {
        const token = localStorage.getItem('token');

        Promise.all([
            axios.get(`${process.env.REACT_APP_API_URL}/hvg-editions`, {
                headers: { Authorization: `Bearer ${token}` },
            }),
            axios.get(`${process.env.REACT_APP_API_URL}/hvg-userEditionOrders/user/${userId}`, {
                headers: { Authorization: `Bearer ${token}` },
            }),
        ])
        .then(([editionRes, orderRes]) => {
            const orderedEditionIds = new Set(orderRes.data.map(o => o.edition?.id));
            const availableEditions = editionRes.data.filter(e => !orderedEditionIds.has(e.id));

            const sorted = [...availableEditions].sort((a, b) =>
                new Date(b.date).getTime() - new Date(a.date).getTime()
            );

            setEditions(sorted);
            if (sorted.length > 0) {
                setSelectedEditionId(sorted[0].id);
            }
        })
        .catch(() => setEditions([]));
    }, []);

    const handleCreate = () => {
        authorizedRequest(
            (token) =>
                axios.post(
                    `${process.env.REACT_APP_API_URL}/hvg-userEditionOrders`,
                    {
                        user: Number(userId),
                        edition: selectedEditionId,
                        defaultOrders: Number(defaultOrders),
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                ),
            () => navigate(`/admin/hvgorders/user/${userId}`),
            () => alert('Creation failed')
        );
    };

    return (
        <Box p={2}>
            <Typography variant="h6">Create HVG Order</Typography>

            <FormControl fullWidth margin="normal">
                <InputLabel id="edition-select-label">Edition</InputLabel>
                <Select
                    labelId="edition-select-label"
                    value={selectedEditionId}
                    label="Edition"
                    onChange={(e) => setSelectedEditionId(e.target.value)}
                >
                    {editions.map((edition) => (
                        <MenuItem key={edition.id} value={edition.id}>
                            {edition.title} ({edition.season})
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <TextField
                label="Orders"
                value={defaultOrders}
                onChange={(e) => setDefaultOrders(e.target.value)}
                fullWidth
                margin="normal"
            />

            <Button variant="contained" onClick={handleCreate}>
                Create
            </Button>
        </Box>
    );
};


export const HvgUserOrderEdit = () => {
    const { userId, orderId } = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [defaultOrders, setDefaultOrders] = useState('');

    useEffect(() => {
        const token = localStorage.getItem('token');
        axios
            .get(`${process.env.REACT_APP_API_URL}/hvg-userEditionOrders/${orderId}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => {
                setOrder(res.data);
                setDefaultOrders(res.data.defaultOrders);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [orderId]);

    const handleSave = () => {
        const token = localStorage.getItem('token');
        axios
            .patch(
                `${process.env.REACT_APP_API_URL}/hvg-userEditionOrders/${orderId}`,
                { defaultOrders },
                { headers: { Authorization: `Bearer ${token}` } }
            )
            .then(() => {
                navigate(`/admin/hvgorders/user/${userId}`); // ✅ Redirect after save
            })
            .catch(() => alert('Update failed'));
    };

    if (loading) return <CircularProgress />;

    return (
        <Box p={2}>
            <Typography variant="h6">Edit Order</Typography>
            <TextField
                label="Edition"
                value={order.edition?.title}
                fullWidth
                margin="normal"
                disabled
            />
            <TextField
                label="Default Orders"
                value={defaultOrders}
                onChange={(e) => setDefaultOrders(e.target.value)}
                fullWidth
                margin="normal"
            />
            <Button variant="contained" onClick={handleSave}>
                Save
            </Button>
        </Box>
    );
};